import {Button, Col, ConfigProvider, Flex, Form, FormProps, Input, Row, Typography} from 'antd';
import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useLocalStorage} from 'react-use';

import {useAuth} from '../../../AuthContext';
import {ApiError, ApiQuery, ApiRuntime} from '../../../gen/api';
import {HttpStatusCode} from '../../lib/net/HttpStatusCode';
import styles from './styles.module.less';

type LoginFields = {
    email: string;
    password: string;
};

const {Title, Text} = Typography;

export const LoginPage: React.FC = () => {
    const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
    const navigate = useNavigate();
    const auth = useAuth();
    const me = ApiQuery.useGetMeMutation();

    const login = ApiQuery.usePostAdminLogInMutation({
        onSuccess: (data) => {
            setToken(data.token);
            ApiRuntime.setApiKey(data.token);
            me.mutateAsync()
                .then((authData) => {
                    auth?.login(data.token!, authData);
                    navigate('/', {replace: true});
                })
                .catch(() => {
                    removeToken();
                    setErrorMsg('Something went wrong. Please try again.');
                });
        },
        onError: (errors) => {
            removeToken();
            // The generated options type the errors as Error[], but the client throws the ApiError[] parsed from the body.
            const tooManyAttempts = (errors as ApiError[] | undefined)?.find(
                (error) => error.code === HttpStatusCode.TOO_MANY_REQUESTS
            );
            setErrorMsg(tooManyAttempts?.message || 'Something went wrong. Please try again.');
        },
    });

    const submitting = login.isLoading || me.isLoading;

    const handleSubmit: FormProps<LoginFields>['onFinish'] = (values) => {
        if (submitting) {
            return;
        }
        setErrorMsg(null);
        login.mutate({params: {email: values.email, password: values.password}});
    };

    const [token, setToken, removeToken] = useLocalStorage('token', '');

    return (
        <>
            <Row className={styles.login}>
                <Col
                    xs={{flex: '100%', order: 2}}
                    lg={{flex: '50%', order: 1}}
                    flex={'auto'}
                >
                    <Flex
                        justify="center"
                        align={'center'}
                        className={styles.loginForm}
                    >
                        <Form
                            name="basic"
                            labelCol={{span: 8}}
                            wrapperCol={{span: 16}}
                            // style={{maxWidth: 600}}
                            initialValues={{remember: true}}
                            onFinish={handleSubmit}
                            autoComplete="off"
                        >
                            <Title
                                level={3}
                                className={styles.logo}
                            >
                                Admin Panel
                            </Title>
                            <Form.Item<LoginFields>
                                label="Email"
                                name="email"
                                rules={[{required: true, message: 'Please input your email!'}]}
                            >
                                <Input
                                    autoFocus
                                    readOnly={submitting}
                                />
                            </Form.Item>

                            <ConfigProvider
                                theme={{
                                    components: {
                                        Form: {
                                            itemMarginBottom: 0,
                                        },
                                    },
                                }}
                            >
                                <Form.Item<LoginFields>
                                    label="Password"
                                    name="password"
                                    rules={[{required: true, message: 'Please input your password!'}]}
                                >
                                    <Input.Password readOnly={submitting} />
                                </Form.Item>
                            </ConfigProvider>

                            <div className={styles.errorMsg}>{errorMsg && <Text type={'danger'}>{errorMsg}</Text>}</div>
                            <Flex justify="end">
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitting}
                                >
                                    Login
                                </Button>
                            </Flex>
                        </Form>
                    </Flex>
                </Col>
                <Col
                    xs={{flex: '100%', order: 1}}
                    lg={{flex: '50%', order: 2}}
                >
                    <Flex
                        justify="center"
                        align={'center'}
                        className={styles.logoWrap}
                    >
                        <div className={styles.logo}>SunShine Auction</div>
                    </Flex>
                </Col>
            </Row>
        </>
    );
};
